/**
 * Retorno de esclarecimento — protocolo entre o modelo e o painel.
 *
 * O mIAu NÃO fica pendurado esperando o usuário: quando falta um dado que ele
 * não pode deduzir, ele ENCERRA o turno emitindo um bloco `miau-ask` na resposta
 * final. O painel lê esse bloco, abre um campo de resposta no lugar do composer
 * e as respostas do usuário voltam num turno novo, comum. Nenhum passo do
 * `LIMIT_STEPS` é gasto esperando gente, e o backend continua stateless.
 *
 * UMA PERGUNTA POR VEZ, UMA IDA AO MODELO. O modelo se contextualiza por inteiro
 * (pedido, anexos, documento), decide TUDO o que falta e manda a lista em
 * `questions`, uma pergunta curta por item. O painel mostra uma de cada vez e
 * só no fim devolve as respostas juntas. Duas alternativas foram descartadas:
 *  - várias perguntas num texto só ("1) modelo; 2) prazo; 3) equipe"): o usuário
 *    lia um parágrafo e respondia tudo num campo;
 *  - uma ida ao modelo por pergunta: segundos de espera entre perguntas, e o
 *    modelo repensando o que já tinha decidido.
 *
 * GUARDA IMPORTANTE: o modelo só sinaliza o TIPO e escreve o texto da pergunta.
 * Rótulo de botão e cópia dos avisos de confirmação são fixos neste arquivo — é
 * o que impede o copiloto de fabricar botões de escolha e devolvê-los ao
 * usuário como se fossem opções dele. Para dado factual, a resposta é texto
 * livre, sempre.
 */
/** Tipos que são confirmação de duas vias (sem campo de texto). */
export function isConfirmKind(kind) {
    return kind === 'scope' || kind === 'offtopic';
}
/**
 * Bloco emitido pelo modelo. Cercado por fence para o `marked` não renderizar
 * nada meia-boca caso algum caminho de exibição escape do parser.
 */
const ASK_BLOCK_RE = /```miau-ask\s*\r?\n([\s\S]*?)```/;
/**
 * Teto de perguntas por rodada. Acima disso o modelo não se contextualizou ou
 * está devolvendo ao usuário decisões que são dele.
 */
export const MAX_QUESTIONS = 3;
/**
 * Teto defensivo de CADA pergunta. O prompt pede uma frase curta; o teto deixa
 * folga para a lista de alternativas que o manual define (ex.: os cinco modelos
 * de contratação), não para parágrafo.
 */
export const MAX_QUESTION_CHARS = 300;
/** Cópia FIXA do aviso de pedido extenso — o modelo não escreve este texto. */
export const SCOPE_QUESTION = 'Esse seu pedido parece muito extenso. Trabalho melhor com pedidos mais ' +
    'diretos e curtos, como gerar um tópico por vez. Quer continuar com seu pedido?';
/**
 * Cópia FIXA da confirmação de conteúdo sem relação com o documento. Genérica
 * de propósito: o modelo não descreve o que acha desconexo — se descrevesse,
 * redigiria o próprio pedido de permissão.
 */
export const OFFTOPIC_QUESTION = 'Esse conteúdo não parece ter relação com o documento. Quer que eu o coloque ' +
    'no documento mesmo assim?';
/** Rótulos dos botões, por tipo. Fixos, nunca vindos do modelo. */
export const ASK_LABELS = {
    clarify: { primary: 'Responder', secondary: 'Ainda não sei', tag: 'Esclarecimento' },
    scope: { primary: 'Sim, continuar', secondary: 'Não, vou separar por partes.', tag: 'Escopo' },
    offtopic: { primary: 'Sim, colocar', secondary: 'Não, deixa como está', tag: 'Confirmação' },
};
/** Rótulo da pergunta no painel: "Esclarecimento · 2 de 3". */
export function askTagText(kind, index, total) {
    const base = ASK_LABELS[kind].tag;
    return total > 1 ? `${base} · ${index + 1} de ${total}` : base;
}
/**
 * Texto enviado como próxima mensagem do usuário em cada saída do painel.
 * São instruções curtas e literais: a resposta chega ao modelo já dizendo o que
 * fazer com ela, sem depender de o system prompt ser lembrado no turno seguinte.
 */
export const ASK_REPLIES = {
    /**
     * Todas as respostas da rodada, juntas e ENQUADRADAS com a pergunta. Crua
     * ("escopo fechado"), uma resposta chegava ao modelo como pedido novo e solto,
     * e ele abria outra rodada em vez de retomar o que tinha sido pedido.
     */
    answers: (itens) => {
        const linhas = itens.map((item, i) => {
            const resposta = item.answer === null
                ? 'ainda não sei. Siga sem esse dado: escreva de forma genérica e correta ou ' +
                    'deixe o campo em branco, sem marcação de preenchimento no texto, e não ' +
                    'pergunte isso de novo neste documento.'
                : item.answer;
            return `${i + 1}. Pergunta: «${item.question}»\n   Resposta: ${resposta}`;
        });
        return ('Respostas aos seus esclarecimentos:\n\n' +
            linhas.join('\n\n') +
            '\n\nRetome o pedido original com isso. Não repita nenhuma dessas perguntas. ' +
            'Só pergunte algo novo se uma resposta acima criou uma dúvida indispensável ' +
            'que não estava nesta lista.');
    },
    /** "Sim, continuar" — o usuário assumiu o pedido extenso. */
    scopeYes: 'Sim, continuar com o pedido como está. Se faltar dado que não esteja no ' +
        'pedido, nos anexos nem no documento, e que não dê para deduzir, pergunte ' +
        'agora; senão, comece.',
    /** "Sim, colocar" — o usuário confirmou o conteúdo sem relação com o documento. */
    offtopicYes: 'Sim, pode colocar esse conteúdo no documento como pedi, mesmo sem relação com ' +
        'o resto. Faça agora, sem perguntar de novo.',
};
/** Limpa, corta no teto e descarta vazias e repetidas. */
function normalizeQuestions(raw) {
    const vistas = new Set();
    const out = [];
    for (const q of raw) {
        if (typeof q !== 'string')
            continue;
        const limpa = q.trim().slice(0, MAX_QUESTION_CHARS).trim();
        if (!limpa || vistas.has(limpa))
            continue;
        vistas.add(limpa);
        out.push(limpa);
        if (out.length === MAX_QUESTIONS)
            break;
    }
    return out;
}
/**
 * Extrai o pedido de esclarecimento da resposta final do modelo.
 * Devolve `null` se não houver bloco, se o JSON for inválido ou se o `kind` não
 * for um dos conhecidos — desconhecido é tratado como texto normal, nunca como
 * um painel improvisado.
 *
 * `clarify` aceita `questions` (lista) e, por compatibilidade, `question`
 * (texto único) — um modelo que ainda use a forma antiga não perde a pergunta.
 */
export function parseAskBlock(text) {
    const m = ASK_BLOCK_RE.exec(text !== null && text !== void 0 ? text : '');
    if (!m)
        return null;
    let raw;
    try {
        raw = JSON.parse(m[1].trim());
    }
    catch (_a) {
        return null; // bloco malformado: o turno vira resposta comum
    }
    if (!raw || typeof raw !== 'object')
        return null;
    const obj = raw;
    if (obj.kind === 'scope')
        return { kind: 'scope', questions: [SCOPE_QUESTION] };
    if (obj.kind === 'offtopic')
        return { kind: 'offtopic', questions: [OFFTOPIC_QUESTION] };
    if (obj.kind === 'clarify') {
        const brutas = Array.isArray(obj.questions) ? obj.questions : [obj.question];
        const questions = normalizeQuestions(brutas);
        if (!questions.length)
            return null; // clarify sem pergunta não é pergunta
        return { kind: 'clarify', questions };
    }
    return null;
}
/**
 * Remove o bloco da resposta, deixando o que o modelo escreveu em volta (o
 * prompt manda não escrever nada, mas isso é instrução, não garantia).
 */
export function stripAskBlock(text) {
    return (text !== null && text !== void 0 ? text : '').replace(ASK_BLOCK_RE, '').trim();
}
